/**
 * V2WizardModal — UX wrapper for POST /api/v2/workspaces/{ws}/wizard/suggest
 *
 * Flow (Feb 2026 — voice-first):
 *   1. "Who is this drafted as?" — Remi / RESILIGENT / Employee Post
 *      └─ if Employee, pick the team member from the User Management list
 *   2. Topic + audience/tone/objective
 *   3. Preview the suggestion; user edits freely
 *   4. "Create card" persists voice_key, post_type, posted_for_* on the card
 */
import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Loader2, Sparkles, Wand2, User, Building2, Users, ArrowLeft, Download, ImageOff, CheckCircle2, ShieldAlert, Ban, AlertTriangle, Eye, Copy, Layers, Star } from "lucide-react";
import { toast } from "sonner";
import { PRIORITY_META } from "./CardPriorityBadge";

// Same-origin in browser so the session cookie (scoped to the page's host) is sent on every API call.
// Falls back to the build-time env var only for SSR / localhost.
const _API_BASE = (typeof window !== 'undefined' && window.location.hostname !== 'localhost') ? window.location.origin : (process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? window.location.origin : ''));
const API = `${_API_BASE}/api`;

// Operator-facing card types. VIDEO removed May 28 2026 — video-gen is
// parked per /app/memory/VIDEO_GEN_PARKED.md, so the option shouldn't
// be discoverable in the wizard. COVER_DESIGN added so proposal cover
// generation lives in the same Draft-with-Rex flow rather than a
// separate page in the Settings drawer.
const CARD_TYPES = ["SOCIAL_POST", "CAROUSEL", "BROCHURE", "ARTICLE", "COVER_DESIGN"];
const CATEGORIES = ["INTERNAL", "COMMERCIAL", "CLIENT_DELIVERY", "ADMIN_FINANCE"];

// Mapping from "Who is this for" → backend voice_key + post_type
const POST_AS_OPTIONS = [
  {
    id: "personal",
    title: "Remi (Personal)",
    description: "Founder voice — warm, vulnerable, story-led. Posts to Remi's LinkedIn.",
    icon: User,
    accent: "border-rose-300 bg-rose-50 text-rose-900",
    accentSelected: "border-rose-500 bg-rose-100 ring-2 ring-rose-300",
    voice_key: "remi_personal",
    post_type: "personal",
  },
  {
    id: "company",
    title: "RESILIGENT (Institutional)",
    description: "Company voice — evidence-led, system-thinking, methodology-anchored. Posts to RESILIGENT page.",
    icon: Building2,
    accent: "border-slate-300 bg-slate-50 text-slate-900",
    accentSelected: "border-slate-700 bg-slate-100 ring-2 ring-slate-400",
    voice_key: "resiligent_institutional",
    post_type: "company",
  },
  {
    id: "employee",
    title: "Employee Post",
    description: "Hybrid voice. Drafted for a named team member to download and post from their own LinkedIn.",
    icon: Users,
    accent: "border-amber-300 bg-amber-50 text-amber-900",
    accentSelected: "border-amber-500 bg-amber-100 ring-2 ring-amber-300",
    voice_key: "employee_voice",
    post_type: "employee",
  },
];

export default function V2WizardModal({ open, onOpenChange, workspaceId, onCardCreated, prefill }) {
  const [step, setStep] = useState("voice"); // voice | prompt | preview
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [postAs, setPostAs] = useState(null); // one of POST_AS_OPTIONS
  const [employeePick, setEmployeePick] = useState({ email: "", name: "" });
  const [teamMembers, setTeamMembers] = useState([]);
  const [form, setForm] = useState({
    topic: "",
    card_type: "SOCIAL_POST",
    audience: "",
    tone: "",
    objective: "",
    market_key: "",
    // Cover-design-only fields (ignored when card_type !== "COVER_DESIGN")
    cover_template: "A",       // A = AI background, B/C = operator photo
    cover_orientation: "a4_portrait",
    cover_scope: "front",
    cover_back_style: "minimalist",
    // Phase 6.2c — three orthogonal cover dimensions (Template A).
    cover_sector_key: "",
    cover_org_type_key: "",
    cover_colour_key: "",
    cover_custom_colour: "",
    // Composition text — Template A uses title/subtitle; B/C use richer sets.
    cover_title: "",
    cover_subtitle: "",
    cover_kicker: "",
    cover_pull_quote: "",
    cover_attribution: "",
    cover_category_label: "",
    cover_tagline: "",
    cover_qr_url: "",
  });
  const [markets, setMarkets] = useState([]);
  const [coverOrientations, setCoverOrientations] = useState([]);
  const [coverSectors, setCoverSectors] = useState([]);
  const [coverOrgTypes, setCoverOrgTypes] = useState([]);
  const [coverColourPresets, setCoverColourPresets] = useState([]);
  // File uploads (kept outside `form` since they're File objects).
  const [coverClientLogo, setCoverClientLogo] = useState(null);   // A: client logo / B: partner logo
  const [coverPhoto, setCoverPhoto] = useState(null);             // B/C: background photo
  const [composing, setComposing] = useState(false);
  const [composeResult, setComposeResult] = useState(null);       // B/C composed cover
  // Composed-prompt preview — dry-run of the three-dimension composer so
  // the operator sees what each dimension contributes BEFORE spending
  // image-gen budget. Same transparency principle as the rewriter diff view.
  const [coverPreview, setCoverPreview] = useState(null);
  const [coverPreviewOpen, setCoverPreviewOpen] = useState(false);
  const [coverPreviewLoading, setCoverPreviewLoading] = useState(false);
  const [coverResult, setCoverResult] = useState(null);  // set when COVER_DESIGN generate succeeds
  // Cover source chooser: generate new vs reuse a saved background.
  const [showSavedPicker, setShowSavedPicker] = useState(false);
  const [savedBgs, setSavedBgs] = useState([]);
  const [savedBgLoading, setSavedBgLoading] = useState(false);
  const [savedBgUsing, setSavedBgUsing] = useState(null);
  const [draft, setDraft] = useState(null);
  const [usedFallback, setUsedFallback] = useState(false);
  // Designer-queue priority chosen at creation (1 highest … 4 lowest).
  // Defaults to P4 (low) per operator decision — every new card starts low
  // and is bumped up deliberately here in the wizard.
  const [draftPriority, setDraftPriority] = useState(4);
  const [prefillNote, setPrefillNote] = useState("");
  // Inline model-decision unblock — set when cover generation returns 412
  // no_active_model_decision. We stash the args used so we can resume the
  // exact same generation after the user one-click activates a model.
  const [modelBlocked, setModelBlocked] = useState(false);
  const [activatingModel, setActivatingModel] = useState(false);
  const [pendingGenArgs, setPendingGenArgs] = useState(null);

  // One-click: activate a sensible default model, then resume generation.
  const quickActivateAndResume = async () => {
    setActivatingModel(true);
    try {
      const r = await fetch(`${API}/phase6/decisions/quick-activate`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(typeof e?.detail === "string" ? e.detail : `Activate failed (${r.status})`);
      }
      const body = await r.json();
      const model = body?.decision?.primary_model || "default model";
      toast.success(`Activated ${model} — resuming generation`);
      setModelBlocked(false);
      const args = pendingGenArgs;
      setPendingGenArgs(null);
      await generate(args || {});
    } catch (e) {
      toast.error(e.message || "Couldn't activate a model");
    } finally {
      setActivatingModel(false);
    }
  };

  // When a prefill arrives (e.g. from Today's calendar widget) seed the topic
  // and bias the tone/market so the user lands on the prompt step ready to
  // hit "Draft with Rex" — no retyping.
  useEffect(() => {
    if (!open || !prefill?.occasion) return;
    const occasion = prefill.occasion;
    const date = prefill.date || "";
    const country = (prefill.country || "").toUpperCase();
    const topic = date
      ? `${occasion} (${date})`
      : occasion;
    const objective = `Mark ${occasion}${country ? ` in ${country}` : ""} with a celebratory, on-brand post that connects the occasion to our work.`;
    setForm((prev) => ({
      ...prev,
      topic,
      objective: prev.objective || objective,
      tone: prev.tone || "warm, celebratory, human",
    }));
    setPrefillNote(`Pre-filled from Today → "${occasion}"${date ? ` on ${date}` : ""}.`);
  }, [open, prefill?.occasion, prefill?.date, prefill?.country]);

  // Dedicated "Create a cover" entry (from the Planner): open the wizard straight
  // into the COVER_DESIGN flow and skip the social "voice" step, which is
  // irrelevant for a proposal cover. Without this, covers were buried behind the
  // post-framed wizard + a Card-type dropdown defaulting to SOCIAL_POST.
  useEffect(() => {
    if (!open || prefill?.card_type !== "COVER_DESIGN") return;
    setForm((prev) => ({ ...prev, card_type: "COVER_DESIGN" }));
    setStep("prompt");
  }, [open, prefill?.card_type]);

  useEffect(() => {
    if (!open) return;
    fetch(`${API}/brand-manager/markets`, { credentials: "include" })
      .then(r => r.ok ? r.json() : []).then(setMarkets).catch(() => {});
    fetch(`${API}/users`, { credentials: "include" })
      .then(r => r.ok ? r.json() : [])
      .then((u) => setTeamMembers(Array.isArray(u) ? u : []))
      .catch(() => {});
    // Load cover orientations from backend (single source of truth for
    // base + upscaled dimensions). Endpoint is admin-gated; non-admins
    // get an empty list which hides the COVER_DESIGN cover-specific UI.
    fetch(`${API}/v2/cover-design/orientations`, { credentials: "include" })
      .then(r => r.ok ? r.json() : { orientations: [] })
      .then((j) => setCoverOrientations(j.orientations || []))
      .catch(() => {});
    // Phase 6.2c — load the three cover dimensions. Defaults applied to
    // the form once the lists arrive (org-type + colour default rows).
    fetch(`${API}/v2/cover-design/sectors`, { credentials: "include" })
      .then(r => r.ok ? r.json() : { sectors: [] })
      .then((j) => setCoverSectors(j.sectors || []))
      .catch(() => {});
    fetch(`${API}/v2/cover-design/org-types`, { credentials: "include" })
      .then(r => r.ok ? r.json() : { org_types: [] })
      .then((j) => {
        const list = j.org_types || [];
        setCoverOrgTypes(list);
        const def = list.find((o) => o.is_default) || list[0];
        if (def) setForm((prev) => prev.cover_org_type_key ? prev : { ...prev, cover_org_type_key: def.org_type_key });
      })
      .catch(() => {});
    fetch(`${API}/v2/cover-design/colour-presets`, { credentials: "include" })
      .then(r => r.ok ? r.json() : { colour_presets: [] })
      .then((j) => {
        const list = j.colour_presets || [];
        setCoverColourPresets(list);
        const def = list.find((c) => c.is_default) || list[0];
        if (def) setForm((prev) => prev.cover_colour_key ? prev : { ...prev, cover_colour_key: def.colour_key });
      })
      .catch(() => {});
  }, [open]);

  // Debounced dry-run of the composed prompt — only while the preview
  // expander is open and a sector is picked. Re-runs as the operator
  // tweaks any dimension so the breakdown stays live. No spend (the
  // /preview-prompt endpoint never generates).
  useEffect(() => {
    if (!coverPreviewOpen || form.card_type !== "COVER_DESIGN" || !form.cover_sector_key) {
      return undefined;
    }
    const ctrl = new AbortController();
    const t = setTimeout(() => {
      setCoverPreviewLoading(true);
      fetch(`${API}/v2/cover-design/preview-prompt`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sector_key: form.cover_sector_key,
          org_type_key: form.cover_org_type_key || null,
          colour_key: form.cover_colour_key || null,
          custom_colour: form.cover_colour_key === "custom" ? form.cover_custom_colour.trim() : null,
          brief: form.topic.trim() || null,
          orientation: form.cover_orientation,
          scope: form.cover_scope,
        }),
        signal: ctrl.signal,
      })
        .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
        .then(setCoverPreview)
        .catch((e) => { if (e.name !== "AbortError") setCoverPreview(null); })
        .finally(() => setCoverPreviewLoading(false));
    }, 350);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [coverPreviewOpen, form.card_type, form.cover_sector_key, form.cover_org_type_key,
    form.cover_colour_key, form.cover_custom_colour, form.cover_orientation, form.cover_scope, form.topic]);

  const reset = () => {
    setStep("voice");
    setLoading(false);
    setCreating(false);
    setPostAs(null);
    setEmployeePick({ email: "", name: "" });
    setForm({
      topic: "", card_type: "SOCIAL_POST",
      audience: "", tone: "", objective: "", market_key: "",
      cover_template: "A", cover_orientation: "a4_portrait", cover_scope: "front",
      cover_sector_key: "", cover_org_type_key: "", cover_colour_key: "", cover_custom_colour: "",
      cover_title: "", cover_subtitle: "", cover_kicker: "", cover_pull_quote: "",
      cover_attribution: "", cover_category_label: "", cover_tagline: "", cover_qr_url: "",
    });
    setCoverClientLogo(null);
    setCoverPhoto(null);
    setComposeResult(null);
    setComposing(false);
    setDraft(null);
    setCoverResult(null);
    setUsedFallback(false);
    setDraftPriority(4);
    setModelBlocked(false);
    setActivatingModel(false);
    setPendingGenArgs(null);
  };

  const handleClose = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const continueFromVoice = () => {
    if (!postAs) {
      toast.error("Pick who this post is being drafted as.");
      return;
    }
    if (postAs.post_type === "employee" && !employeePick.email) {
      toast.error("Pick the team member this post is for.");
      return;
    }
    setStep("prompt");
  };

  // Templates B/C — single-action compose from an uploaded photo. No AI
  // generation, no spend. Returns a finished PNG + PDF the operator
  // downloads straight from the wizard.
  const composePhotoTemplate = async () => {
    const t = form.cover_template;
    if (!coverPhoto) { toast.error("Upload a background photo."); return; }
    if (!form.cover_title.trim()) { toast.error("Enter a title."); return; }
    setComposing(true);
    try {
      const fd = new FormData();
      fd.append("photo", coverPhoto);
      fd.append("title", form.cover_title.trim());
      if (t === "B") {
        if (form.cover_kicker.trim()) fd.append("kicker", form.cover_kicker.trim());
        if (form.cover_pull_quote.trim()) fd.append("pull_quote", form.cover_pull_quote.trim());
        if (form.cover_attribution.trim()) fd.append("attribution", form.cover_attribution.trim());
        if (coverClientLogo) fd.append("partner_logo", coverClientLogo);
      } else {
        if (form.cover_category_label.trim()) fd.append("category_label", form.cover_category_label.trim());
        if (form.cover_subtitle.trim()) fd.append("subtitle", form.cover_subtitle.trim());
        if (form.cover_tagline.trim()) fd.append("tagline", form.cover_tagline.trim());
        if (form.cover_qr_url.trim()) fd.append("qr_url", form.cover_qr_url.trim());
      }
      const endpoint = t === "B" ? "compose-template-b" : "compose-template-c";
      const resp = await fetch(`${API}/v2/cover-design/${endpoint}`, {
        method: "POST", credentials: "include", body: fd,
      });
      if (!resp.ok) {
        const e = await resp.json().catch(() => ({}));
        throw new Error(typeof e?.detail === "string" ? e.detail : `HTTP ${resp.status}`);
      }
      const body = await resp.json();
      setComposeResult({ ...body, template: t });
      setStep("compose_result");
      toast.success(`Template ${t} cover composed`);
    } catch (err) {
      toast.error(`Compose failed: ${err.message}`);
    } finally {
      setComposing(false);
    }
  };

  const generate = async (coverOverrides) => {
    // onClick passes a SyntheticEvent — ignore non-plain-object args so
    // `generate()` and `generate({scope:...})` both behave.
    const ov = (coverOverrides && typeof coverOverrides === "object" && !coverOverrides.nativeEvent) ? coverOverrides : {};
    // ─── Cover Design branch ───────────────────────────────────────────
    // Covers route through /api/v2/cover-design/generate-stream (SSE),
    // not the wizard/suggest endpoint. Covers commonly take 30-100s+
    // which exceeds the 60s ingress timeout on the sync endpoint, so we
    // stream and let variants land progressively. Falls back to sync
    // /generate if the stream fails. Output is a variant grid with
    // downloads, not a card in the planner.
    //
    // Phase 6.2c: the prompt is driven by three orthogonal dimensions
    // (sector + org-type + colour). The free-text `topic` is now an
    // OPTIONAL client-specific addendum, not a required brief.
    if (form.card_type === "COVER_DESIGN") {
      // Templates B & C are operator-photo, single-action (no AI gen):
      // upload photo + text → compose → result. No Library round-trip.
      if (form.cover_template === "B" || form.cover_template === "C") {
        await composePhotoTemplate();
        return;
      }
      // Template A — AI background generation (SSE).
      if (!form.cover_sector_key) {
        toast.error("Pick a sector for the cover background.");
        return;
      }
      const isCustomColour = form.cover_colour_key === "custom";
      if (isCustomColour && !form.cover_custom_colour.trim()) {
        toast.error("Enter a custom colour/palette, or pick a preset.");
        return;
      }
      setLoading(true);
      setModelBlocked(false);
      const briefTrimmed = form.topic.trim();
      const effScope = ov.scope || form.cover_scope;
      const effBackStyle = ov.back_style || form.cover_back_style || "complementary";
      const effVariantCount = ov.variant_count || 2;
      // Front and back are generated as TWO separate short requests so
      // neither exceeds the production gateway (~60-100s) timeout. The first
      // request always does the FRONT only; if the operator chose front+back
      // we stream the back into the SAME run afterwards.
      const wantBack = effScope === "front_back";
      const reqBody = {
        sector_key: form.cover_sector_key,
        org_type_key: form.cover_org_type_key || null,
        colour_key: form.cover_colour_key || null,
        custom_colour: isCustomColour ? form.cover_custom_colour.trim() : null,
        brief: briefTrimmed || null,
        orientation: form.cover_orientation,
        scope: "front",
        back_style: effBackStyle,
        variant_count: effVariantCount,
      };
      const baseResult = {
        run_id: null, orientation: form.cover_orientation, scope: effScope,
        brief: reqBody.brief, sector_key: reqBody.sector_key,
        org_type_key: reqBody.org_type_key, colour_key: reqBody.colour_key,
        rewritten_prompt: "", rules_applied: [],
        decision_id: null, primary_model: null, fallback_model: null,
        front_variants: [], back_variants: [],
        total_cost_usd_estimate: 0,
        // Sides × variants_per_side total — surfaced as a progress pill
        // so the operator sees "2/4 variants" while the rest stream in.
        expected_total: (wantBack ? 2 : 1) * effVariantCount,
      };
      try {
        // 1. Try the SSE streaming endpoint
        const streamResp = await fetch(`${API}/v2/cover-design/generate-stream`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json", "Accept": "text/event-stream" },
          body: JSON.stringify(reqBody),
        });
        if (streamResp.status === 412) { setPendingGenArgs(ov); setModelBlocked(true); return; }
        if (streamResp.status === 429) { toast.error("Image-gen ceiling reached this month"); return; }
        if (!streamResp.ok || !streamResp.body) {
          throw new Error(`stream HTTP ${streamResp.status}`);
        }

        // Show the cover_result step early so the operator sees variants
        // land as they finish, not after the whole run completes.
        let accumulated = { ...baseResult };
        setCoverResult(accumulated);
        setStep("cover_result");

        const reader = streamResp.body.getReader();
        const decoder = new TextDecoder();
        let buf = "";
        let totalCost = 0;
        let runComplete = false;

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          let idx;
          while ((idx = buf.indexOf("\n\n")) >= 0) {
            const chunk = buf.slice(0, idx);
            buf = buf.slice(idx + 2);
            let eventName = "message";
            const dataLines = [];
            for (const line of chunk.split("\n")) {
              if (line.startsWith("event:")) eventName = line.slice(6).trim();
              else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
            }
            if (!dataLines.length) continue;
            let payload = null;
            try { payload = JSON.parse(dataLines.join("\n")); } catch { continue; }

            if (eventName === "run_started") {
              accumulated = {
                ...accumulated,
                run_id: payload.run_id,
                orientation: payload.orientation,
                scope: payload.scope,
                rewritten_prompt: payload.rewritten_prompt,
                rules_applied: payload.rules_applied || [],
                decision_id: payload.decision_id,
                primary_model: payload.primary_model,
                fallback_model: payload.fallback_model,
              };
              setCoverResult({ ...accumulated });
            } else if (eventName === "variant") {
              if (payload.side === "front") accumulated.front_variants.push(payload);
              else if (payload.side === "back") accumulated.back_variants.push(payload);
              totalCost += (payload.cost_usd_estimate || 0);
              accumulated.total_cost_usd_estimate = totalCost;
              setCoverResult({ ...accumulated });
            } else if (eventName === "run_complete") {
              runComplete = true;
              accumulated.total_cost_usd_estimate = payload.total_cost_usd_estimate || totalCost;
              setCoverResult({ ...accumulated });
            } else if (eventName === "error") {
              throw new Error(payload.message || payload.error || "stream error");
            }
          }
        }
        if (!runComplete && (accumulated.front_variants.length + accumulated.back_variants.length) === 0) {
          throw new Error("stream ended with no variants");
        }
        // Front is done. If the operator asked for front+back, stream the
        // matching back side as a SEPARATE short request into the same run,
        // then create ONE planner card holding every variation.
        if (wantBack && accumulated.run_id) {
          try {
            await streamBackForRun(accumulated, effVariantCount, effBackStyle);
          } catch (backErr) {
            console.warn("back-cover stream failed", backErr);
            toast.message("Front covers ready — back covers couldn't be generated; you can add them later.");
          }
        }
        const got = (accumulated.front_variants.length + accumulated.back_variants.length);
        toast.success(`Generated ${got} cover variant${got === 1 ? "" : "s"}`);
        await autoCreateCardFromRun(accumulated);
      } catch (streamErr) {
        console.warn("cover SSE failed, falling back to sync", streamErr);
        toast.message("Stream interrupted — retrying via sync endpoint");
        try {
          const r = await fetch(`${API}/v2/cover-design/generate`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify(reqBody),
          });
          if (r.status === 412) { setPendingGenArgs(ov); setModelBlocked(true); return; }
          if (r.status === 429) { toast.error("Image-gen ceiling reached this month"); return; }
          if (!r.ok) {
            const err = await r.json().catch(() => ({}));
            throw new Error(err?.detail || `Cover gen failed (${r.status})`);
          }
          const body = await r.json();
          setCoverResult(body);
          const totalVariants = (body.front_variants?.length || 0) + (body.back_variants?.length || 0);
          toast.success(`Generated ${totalVariants} cover variants`);
          setStep("cover_result");
        } catch (syncErr) {
          toast.error(`Cover gen failed: ${syncErr.message}`);
        }
      } finally {
        setLoading(false);
      }
      return;
    }
    // ─── Standard social-post branch ───────────────────────────────────
    if (form.topic.trim().length < 5) {
      toast.error("Topic must be at least 5 characters.");
      return;
    }
    setLoading(true);
    try {
      const r = await fetch(`${API}/v2/workspaces/${workspaceId}/wizard/suggest`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ...form, voice_key: postAs?.voice_key || "" }),
      });
      if (!r.ok) throw new Error(`Wizard failed (${r.status})`);
      const body = await r.json();
      const s = body.suggestion;
      setDraft({
        title: s.title || "",
        category: s.category || "INTERNAL",
        card_type: s.card_type || form.card_type,
        graphic_headline: s.graphic_headline || "",
        graphic_subheader: s.graphic_subheader || "",
        body_md: s.body_md || "",
        angle: s.angle || "",
        cta: s.cta || "",
        hashtags: (s.hashtags || []).join(" "),
      });
      setUsedFallback(!!body.used_fallback);
      setStep("preview");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  // Bridge a generated cover variant into an editable card on the board, so it
  // opens in the same full editor used for social posts (move/resize logos +
  // text, fonts, shadows, abstract). The user confirmed covers should land on
  // the board as cards.
  const fetchSavedBackgrounds = async () => {
    setSavedBgLoading(true);
    try {
      const r = await fetch(`${API}/v2/cover-design/saved-backgrounds`, { credentials: "include" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setSavedBgs(j.backgrounds || []);
    } catch (e) {
      toast.error(`Couldn't load saved backgrounds: ${e.message}`);
    } finally {
      setSavedBgLoading(false);
    }
  };

  const applySavedBackgroundOnBoard = async (bg) => {
    setSavedBgUsing(bg.background_id);
    try {
      const cr = await fetch(`${API}/v2/workspaces/${workspaceId}/cards`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: bg.name || `Cover — ${bg.side}`,
          category: "COMMERCIAL", card_type: "COVER_DESIGN",
          graphic_headline: form.cover_title || null,
          graphic_subheader: form.cover_subtitle || null,
          body_md: "", priority: draftPriority, initial_stage: "IN_DESIGN",
        }),
      });
      if (!cr.ok) { const e = await cr.json().catch(() => ({})); throw new Error(e?.detail || `Create failed (${cr.status})`); }
      const card = (await cr.json()).card;
      const br = await fetch(`${API}/v2/workspaces/${workspaceId}/cards/${card.card_id}/image/from-cover`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ saved_background_id: bg.background_id, side: bg.side, format: "a4_portrait" }),
      });
      if (!br.ok) { const e = await br.json().catch(() => ({})); throw new Error(e?.detail || `Bridge failed (${br.status})`); }
      toast.success("Cover added to the planner (In Design) — open it to edit (move text, logos, fonts).");
      onCardCreated && onCardCreated(card);
      handleClose(false);
    } catch (e) {
      toast.error(`Couldn't use that background: ${e.message}`);
    } finally {
      setSavedBgUsing(null);
    }
  };

  const streamBackForRun = async (acc, variantCount, backStyle) => {
    const resp = await fetch(`${API}/v2/cover-design/generate-back-stream`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
      body: JSON.stringify({ run_id: acc.run_id, variant_count: variantCount, back_style: backStyle }),
    });
    if (!resp.ok || !resp.body) throw new Error(`back stream HTTP ${resp.status}`);
    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buf = "";
    let done2 = false;
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      let idx;
      while ((idx = buf.indexOf("\n\n")) >= 0) {
        const chunk = buf.slice(0, idx);
        buf = buf.slice(idx + 2);
        let eventName = "message";
        const dataLines = [];
        for (const line of chunk.split("\n")) {
          if (line.startsWith("event:")) eventName = line.slice(6).trim();
          else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
        }
        if (!dataLines.length) continue;
        let payload = null;
        try { payload = JSON.parse(dataLines.join("\n")); } catch { continue; }
        if (eventName === "variant" && payload.side === "back") {
          acc.back_variants.push(payload);
          acc.total_cost_usd_estimate = (acc.total_cost_usd_estimate || 0) + (payload.cost_usd_estimate || 0);
          setCoverResult({ ...acc });
        } else if (eventName === "run_complete") {
          done2 = true;
          acc.total_cost_usd_estimate = payload.total_cost_usd_estimate || acc.total_cost_usd_estimate;
          setCoverResult({ ...acc });
        } else if (eventName === "error") {
          throw new Error(payload.message || payload.error || "back stream error");
        }
      }
    }
    if (!done2 && acc.back_variants.length === 0) throw new Error("back stream ended with no variants");
  };

  const autoCreateCardFromRun = async (acc) => {
    try {
      const orientation = acc.orientation || form.cover_orientation || "a4_portrait";
      const fmt = (orientation || "").includes("landscape") ? "cover_landscape" : "a4_portrait";
      const coverVariants = [
        ...(acc.front_variants || []).map((v) => ({ side: "front", variant_index: v.variant_index })),
        ...(acc.back_variants || []).map((v) => ({ side: "back", variant_index: v.variant_index })),
      ];
      const title = (form.cover_title || "").trim() || "Cover";
      const cr = await fetch(`${API}/v2/workspaces/${workspaceId}/cards`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title, category: "COMMERCIAL", card_type: "COVER_DESIGN",
          graphic_headline: form.cover_title || null,
          graphic_subheader: form.cover_subtitle || null,
          body_md: "", priority: draftPriority,
          initial_stage: "IN_DESIGN",
        }),
      });
      if (!cr.ok) { const e = await cr.json().catch(() => ({})); throw new Error(e?.detail || `Create failed (${cr.status})`); }
      const card = (await cr.json()).card;
      const br = await fetch(`${API}/v2/workspaces/${workspaceId}/cards/${card.card_id}/image/from-cover`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          run_id: acc.run_id, side: "front", variant_index: 0, format: fmt,
          cover_variants: coverVariants, cover_orientation: orientation,
        }),
      });
      if (!br.ok) { const e = await br.json().catch(() => ({})); throw new Error(e?.detail || `Bridge failed (${br.status})`); }
      toast.success("Cover added to In Design — open it to switch variations, place text & logos.");
      onCardCreated && onCardCreated(card);
      handleClose(false);
    } catch (e) {
      // Don't lose the generated run — fall back to the in-wizard result view
      // so the user can still pick a variant manually.
      toast.error(`Auto-add to planner failed: ${e.message}. Pick a variant below to add it.`);
      setStep("cover_result");
    }
  };

  const editCoverOnBoard = async (runId, side, variantIndex) => {
    try {
      const fmt = (form.cover_orientation || "").includes("landscape") ? "cover_landscape" : "a4_portrait";
      const title = (form.cover_title || "").trim() || `Cover — ${side}`;
      const cr = await fetch(`${API}/v2/workspaces/${workspaceId}/cards`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title, category: "COMMERCIAL", card_type: "COVER_DESIGN",
          graphic_headline: form.cover_title || null,
          graphic_subheader: form.cover_subtitle || null,
          body_md: "", priority: draftPriority,
          initial_stage: "IN_DESIGN",
        }),
      });
      if (!cr.ok) { const e = await cr.json().catch(() => ({})); throw new Error(e?.detail || `Create failed (${cr.status})`); }
      const card = (await cr.json()).card;
      const br = await fetch(`${API}/v2/workspaces/${workspaceId}/cards/${card.card_id}/image/from-cover`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ run_id: runId, side, variant_index: variantIndex, format: fmt }),
      });
      if (!br.ok) { const e = await br.json().catch(() => ({})); throw new Error(e?.detail || `Bridge failed (${br.status})`); }
      toast.success("Cover added to the planner (In Design) — open it to edit (move logos, fonts, shadows).");
      onCardCreated && onCardCreated(card);
      handleClose(false);
    } catch (e) {
      toast.error(`Couldn't open cover in editor: ${e.message}`);
    }
  };

  const createCard = async () => {
    setCreating(true);
    try {
      const payload = {
        title: draft.title,
        category: draft.category,
        card_type: draft.card_type,
        graphic_headline: draft.graphic_headline || null,
        graphic_subheader: draft.graphic_subheader || null,
        body_md: draft.body_md,
        angle: draft.angle || null,
        cta: draft.cta || null,
        hashtags: draft.hashtags.split(/\s+/).filter(Boolean),
        voice_key: postAs?.voice_key || null,
        post_type: postAs?.post_type || null,
        posted_for_email: postAs?.post_type === "employee" ? employeePick.email : null,
        posted_for_name: postAs?.post_type === "employee" ? employeePick.name : null,
        due_date: prefill?.date || null,
        priority: draftPriority,
      };
      const r = await fetch(`${API}/v2/workspaces/${workspaceId}/cards`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err?.detail || `Create failed (${r.status})`);
      }
      const body = await r.json();
      const createdCard = body.card;
      toast.success("Card created.");

      onCardCreated && onCardCreated(createdCard);
      handleClose(false);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setCreating(false);
    }
  };

  // Refine the draft body in-place via Claude before the card is persisted.
  const [refineInstruction, setRefineInstruction] = useState("");
  const [refining, setRefining] = useState(false);
  const refineDraftBody = async () => {
    const instr = refineInstruction.trim();
    if (!instr || refining) return;
    if (!draft?.body_md?.trim()) {
      toast.error("Body is empty — there's nothing to refine yet");
      return;
    }
    setRefining(true);
    try {
      const r = await fetch(`${API}/v2/workspaces/${workspaceId}/refine-text`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ instruction: instr, current_body: draft.body_md }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body?.detail || `Refine failed (${r.status})`);
      if (body.used_fallback) {
        toast.warning("Rex couldn't refine — kept the current body");
      } else {
        setDraft((prev) => ({ ...prev, body_md: body.body }));
        setRefineInstruction("");
        toast.success("Rex refined the post");
      }
    } catch (e) {
      toast.error(e.message || "Refine failed");
    } finally {
      setRefining(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="v2-wizard-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900">
            <Sparkles className="h-5 w-5 text-indigo-600" />
            {step === "voice"   && "Who is this drafted as?"}
            {step === "prompt"  && (form.card_type === "COVER_DESIGN" ? "Draft a proposal cover" : "Draft a card with Rex")}
            {step === "preview" && "Review your draft"}
            {step === "cover_result" && "Cover variants"}
            {step === "compose_result" && "Composed cover"}
          </DialogTitle>
        </DialogHeader>

        {step === "voice" && (
          <div className="space-y-4" data-testid="wizard-voice-step">
            <p className="text-sm text-slate-600">
              Picking the voice up front means Rex drafts in the right tone and the card surfaces with the right author.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {POST_AS_OPTIONS.map((opt) => {
                const Icon = opt.icon;
                const selected = postAs?.id === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setPostAs(opt)}
                    className={`text-left p-4 rounded-lg border transition ${selected ? opt.accentSelected : opt.accent + " hover:opacity-90"}`}
                    data-testid={`wizard-voice-${opt.id}`}
                  >
                    <Icon className="w-5 h-5 mb-2" />
                    <div className="font-semibold text-sm mb-1">{opt.title}</div>
                    <div className="text-xs leading-relaxed opacity-80">{opt.description}</div>
                  </button>
                );
              })}
            </div>

            {postAs?.post_type === "employee" && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-3" data-testid="employee-picker">
                <Label className="text-amber-900 text-sm font-semibold">Drafted for…</Label>
                <select
                  className="w-full rounded-md border border-amber-300 bg-white px-3 py-2 text-sm text-slate-900"
                  value={employeePick.email}
                  onChange={(e) => {
                    const tm = teamMembers.find(t => (t.email || "").toLowerCase() === e.target.value);
                    setEmployeePick({
                      email: e.target.value,
                      name: tm?.name || tm?.full_name || tm?.email || "",
                    });
                  }}
                  data-testid="employee-picker-select"
                >
                  <option value="">— Select team member —</option>
                  {teamMembers.map((t) => (
                    <option key={t.user_id || t.email} value={(t.email || "").toLowerCase()}>
                      {t.name || t.full_name || t.email} {t.role ? `· ${t.role}` : ""}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-amber-800">
                  Limit: 2 Employee Posts per person per rolling 7 days. They'll download the bundle and post from their own LinkedIn.
                </p>
              </div>
            )}
          </div>
        )}

        {step === "prompt" && (
          <div className="space-y-4" data-testid="wizard-prompt-step">
            {modelBlocked && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 space-y-2" data-testid="wizard-model-blocked-banner">
                <div className="flex items-start gap-2">
                  <ShieldAlert className="h-4 w-4 mt-0.5 flex-none text-amber-700" />
                  <div className="text-xs text-amber-900 leading-relaxed">
                    <span className="font-semibold">Image generation is blocked.</span> No image model is
                    active for this environment yet. Activate a sensible default to generate now — it's
                    recorded for the audit trail and you can refine the choice later on the Model Comparison page.
                  </div>
                </div>
                <Button
                  type="button"
                  size="sm"
                  onClick={quickActivateAndResume}
                  disabled={activatingModel}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="wizard-use-this-model-btn"
                >
                  {activatingModel
                    ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Activating…</>
                    : <><CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Use this model &amp; continue</>}
                </Button>
              </div>
            )}
            {prefillNote && (
              <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="wizard-prefill-banner">
                <Sparkles className="h-3.5 w-3.5 mt-0.5 flex-none" />
                <span>{prefillNote} You can still edit anything before drafting.</span>
              </div>
            )}
            {postAs && (
              <div className="flex items-center gap-2 text-xs">
                <Badge className="bg-indigo-100 text-indigo-800">{postAs.title}</Badge>
                {postAs.post_type === "employee" && employeePick.name && (
                  <Badge className="bg-amber-100 text-amber-900">Drafted for: {employeePick.name}</Badge>
                )}
              </div>
            )}
            {form.card_type === "COVER_DESIGN" && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="wizard-cover-source-chooser">
                <div className="text-xs font-semibold text-slate-700 mb-2">How do you want to make this cover?</div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setShowSavedPicker(false)}
                    className={`rounded-md border px-3 py-2 text-xs text-left transition ${!showSavedPicker ? "border-blue-500 bg-blue-50 ring-1 ring-blue-400" : "border-slate-300 bg-white hover:bg-slate-50"}`}
                    data-testid="wizard-cover-source-generate"
                  >
                    <div className="font-semibold flex items-center gap-1"><Sparkles className="h-3.5 w-3.5" /> Generate new</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">Create a fresh AI background below.</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowSavedPicker(true); if (savedBgs.length === 0) fetchSavedBackgrounds(); }}
                    className={`rounded-md border px-3 py-2 text-xs text-left transition ${showSavedPicker ? "border-amber-500 bg-amber-50 ring-1 ring-amber-400" : "border-slate-300 bg-white hover:bg-slate-50"}`}
                    data-testid="wizard-cover-source-saved"
                  >
                    <div className="font-semibold flex items-center gap-1"><Star className="h-3.5 w-3.5" /> Use a saved one</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">Reuse a saved / uploaded background.</div>
                  </button>
                </div>

                {showSavedPicker && (
                  <div className="mt-3" data-testid="wizard-saved-picker">
                    {savedBgLoading ? (
                      <div className="text-xs text-slate-500 flex items-center gap-2 py-4 justify-center"><Loader2 className="h-3 w-3 animate-spin" /> Loading saved backgrounds…</div>
                    ) : savedBgs.length === 0 ? (
                      <div className="text-xs text-slate-500 italic text-center py-4" data-testid="wizard-saved-empty">
                        No saved backgrounds yet. Generate a cover, then ⭐ Save it (or upload one) from the Cover Library.
                      </div>
                    ) : (
                      <div className="grid grid-cols-3 gap-2 max-h-72 overflow-y-auto">
                        {savedBgs.map((bg) => {
                          const root = API.replace(/\/api$/, "");
                          return (
                            <div key={bg.background_id} className="rounded border border-slate-200 bg-white p-1 space-y-1" data-testid={`wizard-saved-item-${bg.background_id}`}>
                              <img src={`${root}${bg.preview_url}?thumb=1`} loading="lazy" alt={bg.name} className="w-full h-20 object-cover rounded border border-slate-100" />
                              <div className="text-[10px] font-medium text-slate-700 truncate" title={bg.name}>{bg.name}</div>
                              <div className="text-[9px] uppercase text-slate-400">{bg.side}{bg.source === "uploaded" ? " · up" : ""}</div>
                              <button
                                type="button"
                                onClick={() => applySavedBackgroundOnBoard(bg)}
                                disabled={savedBgUsing === bg.background_id}
                                className="w-full text-[10px] bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white rounded px-1 py-1 flex items-center justify-center gap-1"
                                data-testid={`wizard-saved-use-${bg.background_id}`}
                              >
                                {savedBgUsing === bg.background_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />} Use this
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <p className="text-[10px] text-slate-500 mt-2">Picking one adds it to the planner (In Design) and opens the editor so you can place text & logos.</p>
                  </div>
                )}
              </div>
            )}
            {(form.card_type !== "COVER_DESIGN" || form.cover_template === "A") && (
            <div>
              <Label htmlFor="topic">{form.card_type === "COVER_DESIGN" ? "Client-specific context (optional)" : "What's the post about?"}</Label>
              <Textarea
                id="topic"
                value={form.topic}
                onChange={(e) => setForm({ ...form, topic: e.target.value })}
                placeholder={form.card_type === "COVER_DESIGN"
                  ? "Optional: client / programme specifics to layer on top of the sector style. e.g. for a Lagos infrastructure mandate."
                  : "e.g. Why boards underestimate CEO succession timing"}
                className="mt-1"
                rows={form.card_type === "COVER_DESIGN" ? 2 : 3}
                data-testid="wizard-topic-input"
              />
              {form.card_type === "COVER_DESIGN" && (
                <p className="text-[11px] text-slate-500 mt-1">
                  The sector, organisation type and colour below drive the look. This box just adds
                  client-specific context — leave it blank to use the sector style as-is. Output is a
                  downloadable PNG (no people, no rendered text).
                </p>
              )}
            </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="card_type">Card type</Label>
                <select
                  id="card_type"
                  value={form.card_type}
                  onChange={(e) => setForm({ ...form, card_type: e.target.value })}
                  className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                  data-testid="wizard-card-type-select"
                >
                  {CARD_TYPES.map((t) => (
                    <option key={t} value={t}>{t === "COVER_DESIGN" ? "COVER_DESIGN (A4 / landscape background)" : t}</option>
                  ))}
                </select>
              </div>
              {form.card_type === "COVER_DESIGN" ? (
                <>
                  <div className="col-span-2">
                    <Label htmlFor="cover_template">Template</Label>
                    <select
                      id="cover_template"
                      value={form.cover_template}
                      onChange={(e) => setForm({ ...form, cover_template: e.target.value })}
                      className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                      data-testid="wizard-cover-template-select"
                    >
                      <option value="A">A — Engagement Proposal (AI background)</option>
                      <option value="B">B — Thought-Leadership (your photo)</option>
                      <option value="C">C — Corporate Profile (your photo)</option>
                    </select>
                  </div>

                  <div className={form.cover_template === "A" ? "col-span-2" : ""}>
                    <Label htmlFor="cover_title">Title</Label>
                    <Input
                      id="cover_title"
                      value={form.cover_title}
                      onChange={(e) => setForm({ ...form, cover_title: e.target.value })}
                      placeholder="e.g. Resilience As Strategy"
                      className="mt-1"
                      data-testid="wizard-cover-title-input"
                    />
                  </div>
                  {(form.cover_template === "A" || form.cover_template === "C") && (
                    <div className={form.cover_template === "A" ? "col-span-2" : ""}>
                      <Label htmlFor="cover_subtitle">Subtitle (optional)</Label>
                      <Input
                        id="cover_subtitle"
                        value={form.cover_subtitle}
                        onChange={(e) => setForm({ ...form, cover_subtitle: e.target.value })}
                        placeholder="e.g. A board briefing"
                        className="mt-1"
                        data-testid="wizard-cover-subtitle-input"
                      />
                    </div>
                  )}

                  {/* ── Template A — AI background (3 orthogonal dimensions) ── */}
                  {form.cover_template === "A" && (
                  <>
                  <div>
                    <Label htmlFor="cover_sector">Sector</Label>
                    <select
                      id="cover_sector"
                      value={form.cover_sector_key}
                      onChange={(e) => setForm({ ...form, cover_sector_key: e.target.value })}
                      className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                      data-testid="wizard-cover-sector-select"
                    >
                      <option value="">— Select sector —</option>
                      {coverSectors.map((s) => (
                        <option key={s.sector_key} value={s.sector_key}>
                          {s.label}{s.evidence_backed ? " ★" : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="cover_org_type">Organisation type</Label>
                    <select
                      id="cover_org_type"
                      value={form.cover_org_type_key}
                      onChange={(e) => setForm({ ...form, cover_org_type_key: e.target.value })}
                      className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                      data-testid="wizard-cover-org-type-select"
                    >
                      {coverOrgTypes.map((o) => (
                        <option key={o.org_type_key} value={o.org_type_key}>{o.display_label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="cover_colour">Colour scope</Label>
                    <select
                      id="cover_colour"
                      value={form.cover_colour_key}
                      onChange={(e) => setForm({ ...form, cover_colour_key: e.target.value })}
                      className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                      data-testid="wizard-cover-colour-select"
                    >
                      {coverColourPresets.map((c) => (
                        <option key={c.colour_key} value={c.colour_key}>{c.display_label}</option>
                      ))}
                    </select>
                  </div>
                  {form.cover_colour_key === "custom" && (
                    <div className="col-span-2">
                      <Label htmlFor="cover_custom_colour">Custom palette</Label>
                      <Input
                        id="cover_custom_colour"
                        value={form.cover_custom_colour}
                        onChange={(e) => setForm({ ...form, cover_custom_colour: e.target.value })}
                        placeholder='e.g. "deep teal + warm sand" or "#0B1E5B #C8A45C"'
                        className="mt-1"
                        data-testid="wizard-cover-custom-colour-input"
                      />
                    </div>
                  )}
                  <div>
                    <Label htmlFor="cover_orientation">Orientation</Label>
                    <select
                      id="cover_orientation"
                      value={form.cover_orientation}
                      onChange={(e) => setForm({ ...form, cover_orientation: e.target.value })}
                      className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                      data-testid="wizard-cover-orientation-select"
                    >
                      {coverOrientations.length === 0 ? (
                        <option value="a4_portrait">A4 portrait — 2480×3508px</option>
                      ) : coverOrientations.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.label} — {o.upscaled_wh[0]}×{o.upscaled_wh[1]}px
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="cover_scope">Scope</Label>
                    <select
                      id="cover_scope"
                      value={form.cover_scope}
                      onChange={(e) => setForm({ ...form, cover_scope: e.target.value })}
                      className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                      data-testid="wizard-cover-scope-select"
                    >
                      <option value="front">Front only (2 variants)</option>
                      <option value="front_back">Front + back (2 each · generated separately)</option>
                    </select>
                  </div>

                  {form.cover_scope === "front_back" && (
                    <div>
                      <Label htmlFor="cover_back_style">Back page style</Label>
                      <select
                        id="cover_back_style"
                        value={form.cover_back_style}
                        onChange={(e) => setForm({ ...form, cover_back_style: e.target.value })}
                        className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                        data-testid="wizard-cover-back-style-select"
                      >
                        <option value="minimalist">Minimalist (clean, mostly empty)</option>
                        <option value="complementary">Complementary (matches front weight)</option>
                      </select>
                    </div>
                  )}

                  {/* Composed-prompt preview — see what each dimension
                      contributes before spending image-gen budget. */}
                  <div className="col-span-2 border border-slate-200 rounded-md bg-slate-50/60">
                    <button
                      type="button"
                      onClick={() => setCoverPreviewOpen((v) => !v)}
                      disabled={!form.cover_sector_key}
                      className="w-full flex items-center justify-between px-3 py-2 text-left text-xs font-medium text-slate-700 disabled:opacity-50"
                      data-testid="wizard-cover-preview-toggle"
                    >
                      <span className="flex items-center gap-1.5">
                        <Eye className="w-3.5 h-3.5" />
                        Preview composed prompt
                        {coverPreviewLoading && <Loader2 className="w-3 h-3 animate-spin" />}
                      </span>
                      <span className="text-slate-400">{coverPreviewOpen ? "Hide" : "Show"}</span>
                    </button>
                    {coverPreviewOpen && (
                      <div className="px-3 pb-3 space-y-2" data-testid="wizard-cover-preview-body">
                        {!form.cover_sector_key ? (
                          <p className="text-[11px] text-slate-500">Pick a sector to preview.</p>
                        ) : !coverPreview ? (
                          <p className="text-[11px] text-slate-500">{coverPreviewLoading ? "Composing…" : "No preview yet."}</p>
                        ) : (
                          <>
                            <p className="text-[10px] text-slate-500">
                              Assembled in order: sector → org-type → colour → context → anti-instructions.
                              This is what the model sees (before the rewriter pass).
                            </p>
                            {coverPreview.segments?.map((seg, i) => {
                              const cls = {
                                sector: "border-indigo-300 bg-indigo-50 text-indigo-900",
                                org_type: "border-amber-300 bg-amber-50 text-amber-900",
                                colour: "border-emerald-300 bg-emerald-50 text-emerald-900",
                                addendum: "border-slate-300 bg-white text-slate-800",
                                anti_instructions: "border-rose-200 bg-rose-50 text-rose-900",
                              }[seg.dimension] || "border-slate-300 bg-white text-slate-800";
                              return (
                                <div key={i} className={`rounded border px-2 py-1.5 text-[11px] ${cls}`} data-testid={`wizard-cover-preview-seg-${seg.dimension}`}>
                                  <div className="flex items-center justify-between mb-0.5 gap-2">
                                    <div className="text-[9px] font-semibold uppercase tracking-wide opacity-70">{seg.label}</div>
                                    {["sector", "org_type", "colour"].includes(seg.dimension) && (
                                      <button
                                        type="button"
                                        onClick={async () => {
                                          try {
                                            await navigator.clipboard.writeText(seg.text);
                                            toast.success(`Copied ${seg.label} text — lift it verbatim into the seed`);
                                          } catch {
                                            toast.error("Clipboard unavailable");
                                          }
                                        }}
                                        className="shrink-0 text-[9px] flex items-center gap-0.5 opacity-60 hover:opacity-100 transition-opacity"
                                        title={`Copy only the ${seg.label} text (for verbatim lift into the seed when this earns evidence-backed)`}
                                        data-testid={`wizard-cover-preview-copy-${seg.dimension}`}
                                      >
                                        <Copy className="w-3 h-3" /> Copy
                                      </button>
                                    )}
                                  </div>
                                  <div className="leading-snug">{seg.text}</div>
                                </div>
                              );
                            })}
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Template A — client logo (composited bottom-left on a
                      darkness-matched clean zone) */}
                  <div className="col-span-2">
                    <Label htmlFor="cover_client_logo">Client logo (optional — composited bottom-left)</Label>
                    <input
                      type="file" accept="image/png,image/jpeg,image/webp" id="cover_client_logo"
                      onChange={(e) => setCoverClientLogo(e.target.files?.[0] || null)}
                      className="mt-1 block w-full text-xs text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-slate-800 file:text-white file:text-xs hover:file:bg-slate-900"
                      data-testid="wizard-cover-client-logo-input"
                    />
                    {coverClientLogo && <p className="text-[11px] text-emerald-700 mt-1">{coverClientLogo.name}</p>}
                  </div>
                  </>
                  )}

                  {/* ── Template B — Thought-Leadership (operator photo) ── */}
                  {form.cover_template === "B" && (
                  <>
                    <div className="col-span-2">
                      <Label htmlFor="cover_photo_b">Background photo (required)</Label>
                      <input
                        type="file" accept="image/png,image/jpeg,image/webp" id="cover_photo_b"
                        onChange={(e) => setCoverPhoto(e.target.files?.[0] || null)}
                        className="mt-1 block w-full text-xs text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-slate-800 file:text-white file:text-xs hover:file:bg-slate-900"
                        data-testid="wizard-cover-photo-input"
                      />
                      {coverPhoto && <p className="text-[11px] text-emerald-700 mt-1">{coverPhoto.name}</p>}
                    </div>
                    <div>
                      <Label htmlFor="cover_kicker">Kicker (optional)</Label>
                      <Input id="cover_kicker" value={form.cover_kicker}
                        onChange={(e) => setForm({ ...form, cover_kicker: e.target.value })}
                        placeholder="e.g. Board Advisory" className="mt-1"
                        data-testid="wizard-cover-kicker-input" />
                    </div>
                    <div>
                      <Label htmlFor="cover_attribution">Attribution (optional)</Label>
                      <Input id="cover_attribution" value={form.cover_attribution}
                        onChange={(e) => setForm({ ...form, cover_attribution: e.target.value })}
                        placeholder="e.g. Remi Adebonojo, Founder" className="mt-1"
                        data-testid="wizard-cover-attribution-input" />
                    </div>
                    <div className="col-span-2">
                      <Label htmlFor="cover_pull_quote">Pull-quote (optional)</Label>
                      <Textarea id="cover_pull_quote" value={form.cover_pull_quote}
                        onChange={(e) => setForm({ ...form, cover_pull_quote: e.target.value })}
                        placeholder="The headline insight, in the operator's words." rows={2} className="mt-1"
                        data-testid="wizard-cover-pull-quote-input" />
                    </div>
                    <div className="col-span-2">
                      <Label htmlFor="cover_partner_logo">Partner logo (optional — co-branded lock-up)</Label>
                      <input
                        type="file" accept="image/png,image/jpeg,image/webp" id="cover_partner_logo"
                        onChange={(e) => setCoverClientLogo(e.target.files?.[0] || null)}
                        className="mt-1 block w-full text-xs text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-slate-800 file:text-white file:text-xs hover:file:bg-slate-900"
                        data-testid="wizard-cover-partner-logo-input"
                      />
                      {coverClientLogo && <p className="text-[11px] text-emerald-700 mt-1">{coverClientLogo.name}</p>}
                    </div>
                  </>
                  )}

                  {/* ── Template C — Corporate Profile (operator photo) ── */}
                  {form.cover_template === "C" && (
                  <>
                    <div className="col-span-2">
                      <Label htmlFor="cover_photo_c">Background photo (required)</Label>
                      <input
                        type="file" accept="image/png,image/jpeg,image/webp" id="cover_photo_c"
                        onChange={(e) => setCoverPhoto(e.target.files?.[0] || null)}
                        className="mt-1 block w-full text-xs text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-slate-800 file:text-white file:text-xs hover:file:bg-slate-900"
                        data-testid="wizard-cover-photo-input"
                      />
                      {coverPhoto && <p className="text-[11px] text-emerald-700 mt-1">{coverPhoto.name}</p>}
                    </div>
                    <div>
                      <Label htmlFor="cover_category">Category label (optional)</Label>
                      <Input id="cover_category" value={form.cover_category_label}
                        onChange={(e) => setForm({ ...form, cover_category_label: e.target.value })}
                        placeholder="e.g. Corporate Profile" className="mt-1"
                        data-testid="wizard-cover-category-input" />
                    </div>
                    <div>
                      <Label htmlFor="cover_qr">QR link (optional)</Label>
                      <Input id="cover_qr" value={form.cover_qr_url}
                        onChange={(e) => setForm({ ...form, cover_qr_url: e.target.value })}
                        placeholder="https://resiligent.com" className="mt-1"
                        data-testid="wizard-cover-qr-input" />
                    </div>
                    <div className="col-span-2">
                      <Label htmlFor="cover_tagline">Tagline (optional)</Label>
                      <Input id="cover_tagline" value={form.cover_tagline}
                        onChange={(e) => setForm({ ...form, cover_tagline: e.target.value })}
                        placeholder="e.g. Prepared for senior leadership" className="mt-1"
                        data-testid="wizard-cover-tagline-input" />
                    </div>
                  </>
                  )}
                </>
              ) : (
                <>
                  <div>
                    <Label htmlFor="audience">Audience (optional)</Label>
                    <Input
                      id="audience"
                      value={form.audience}
                      onChange={(e) => setForm({ ...form, audience: e.target.value })}
                      placeholder="e.g. Listed-company directors"
                      className="mt-1"
                      data-testid="wizard-audience-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="tone">Tone (optional)</Label>
                    <Input
                      id="tone"
                      value={form.tone}
                      onChange={(e) => setForm({ ...form, tone: e.target.value })}
                      placeholder="e.g. evidence-led, no fluff"
                      className="mt-1"
                      data-testid="wizard-tone-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="objective">Objective (optional)</Label>
                    <Input
                      id="objective"
                      value={form.objective}
                      onChange={(e) => setForm({ ...form, objective: e.target.value })}
                      placeholder="e.g. drive demo bookings"
                      className="mt-1"
                      data-testid="wizard-objective-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="market">Market (optional)</Label>
                    <select
                      id="market"
                      value={form.market_key}
                      onChange={(e) => setForm({ ...form, market_key: e.target.value })}
                      className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                      data-testid="wizard-market-select"
                    >
                      <option value="">— Default —</option>
                      {markets.map((m) => <option key={m.key} value={m.key}>{m.name}</option>)}
                    </select>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {step === "cover_result" && coverResult && (
          <CoverResultPanel result={coverResult} apiBase={API}
            onEditOnBoard={editCoverOnBoard}
            composeMeta={{ title: form.cover_title, subtitle: form.cover_subtitle, clientLogo: coverClientLogo }} />
        )}
        {step === "compose_result" && composeResult && (
          <ComposeResultPanel result={composeResult} apiBase={API} />
        )}

        {step === "preview" && draft && (
          <div className="space-y-3" data-testid="wizard-preview-step">
            {usedFallback && (
              <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
                LLM unavailable — used heuristic fallback. Review carefully before posting.
              </div>
            )}
            {postAs && (
              <div className="flex items-center gap-2 text-xs">
                <Badge className="bg-indigo-100 text-indigo-800">{postAs.title}</Badge>
                {postAs.post_type === "employee" && employeePick.name && (
                  <Badge className="bg-amber-100 text-amber-900">Drafted for: {employeePick.name}</Badge>
                )}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Title</Label>
                <Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} className="mt-1" data-testid="wizard-draft-title" />
              </div>
              <div>
                <Label>Category</Label>
                <select
                  value={draft.category}
                  onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                  className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                  data-testid="wizard-draft-category"
                >
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div className="rounded-lg border border-teal-200 bg-teal-50/60 p-3 space-y-2" data-testid="wizard-graphic-copy">
              <Label className="text-[10px] uppercase tracking-wider text-teal-800 font-bold flex items-center gap-1">
                Graphic copy — goes INTO the designed graphic
              </Label>
              <div>
                <Label className="text-xs text-slate-600">Headline</Label>
                <Input value={draft.graphic_headline} onChange={(e) => setDraft({ ...draft, graphic_headline: e.target.value })} className="mt-1" placeholder="Punchy 3–8 word headline for the graphic" data-testid="wizard-draft-headline" />
              </div>
              <div>
                <Label className="text-xs text-slate-600">Sub-header</Label>
                <Input value={draft.graphic_subheader} onChange={(e) => setDraft({ ...draft, graphic_subheader: e.target.value })} className="mt-1" placeholder="Short supporting line for the graphic" data-testid="wizard-draft-subheader" />
              </div>
            </div>
            <div>
              <Label className="flex items-center gap-2">Post caption <span className="text-[10px] font-normal text-emerald-700 normal-case">(clean — ready to publish, no headline repeated)</span></Label>
              <Textarea value={draft.body_md} onChange={(e) => setDraft({ ...draft, body_md: e.target.value })} rows={8} className="mt-1 font-mono text-xs" data-testid="wizard-draft-body" />
              <div className="mt-2 flex items-end gap-2">
                <div className="flex-1">
                  <Label className="text-[10px] uppercase tracking-wider text-purple-700 font-semibold">Refine with Rex</Label>
                  <Input
                    value={refineInstruction}
                    onChange={(e) => setRefineInstruction(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey && refineInstruction.trim() && !refining) {
                        e.preventDefault();
                        refineDraftBody();
                      }
                    }}
                    placeholder="e.g. make this more celebratory for May Day"
                    disabled={refining}
                    className="mt-1 text-sm"
                    data-testid="wizard-refine-input"
                  />
                </div>
                <Button
                  type="button"
                  size="sm"
                  onClick={refineDraftBody}
                  disabled={refining || !refineInstruction.trim()}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  data-testid="wizard-refine-btn"
                >
                  {refining ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-3.5 w-3.5 mr-1" />Refine</>}
                </Button>
              </div>
              <p className="text-[10px] text-slate-500 mt-1">Rex rewrites the body using Claude. Press Enter to apply.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Angle</Label>
                <Input value={draft.angle} onChange={(e) => setDraft({ ...draft, angle: e.target.value })} className="mt-1" data-testid="wizard-draft-angle" />
              </div>
              <div>
                <Label>CTA</Label>
                <Input value={draft.cta} onChange={(e) => setDraft({ ...draft, cta: e.target.value })} className="mt-1" data-testid="wizard-draft-cta" />
              </div>
            </div>
            <div>
              <Label>Hashtags (space-separated)</Label>
              <Input value={draft.hashtags} onChange={(e) => setDraft({ ...draft, hashtags: e.target.value })} className="mt-1 font-mono text-xs" data-testid="wizard-draft-hashtags" />
            </div>
            <div className="pt-1">
              <Label className="text-xs">Priority</Label>
              <p className="text-[11px] text-slate-500 mb-1.5">
                Where should this sit in the designer queue? Defaults to P4 (low) — bump it up if it's urgent.
              </p>
              <div className="flex items-center gap-1.5" data-testid="wizard-priority-picker">
                {[1, 2, 3, 4].map((p) => {
                  const m = PRIORITY_META[p];
                  const active = draftPriority === p;
                  return (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setDraftPriority(p)}
                      title={m.title}
                      data-testid={`wizard-priority-p${p}`}
                      className={`text-[11px] uppercase tracking-wider font-bold rounded border px-2.5 py-1 transition ${m.cls} ${active ? "ring-2 ring-indigo-500" : "opacity-70 hover:opacity-100"}`}
                    >
                      {m.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          {step === "voice" && (
            <Button onClick={continueFromVoice} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="wizard-continue-btn">
              Continue
            </Button>
          )}
          {step === "prompt" && (
            <>
              <Button variant="ghost" onClick={() => setStep("voice")} data-testid="wizard-back-voice"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
              {form.card_type === "COVER_DESIGN" && form.cover_template === "A" && (
                <Button
                  onClick={() => generate({ scope: "front_back", back_style: "minimalist", variant_count: 1 })}
                  disabled={loading || composing || !form.cover_sector_key}
                  variant="outline"
                  className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                  data-testid="wizard-cover-set-btn"
                  title="Generate a matched front + minimalist back pair in one click"
                >
                  {(loading || composing) ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Layers className="h-4 w-4 mr-2" />}
                  Cover set (front + back)
                </Button>
              )}
              <Button onClick={() => generate()} disabled={loading || composing} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="wizard-generate-btn">
                {(loading || composing) ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Wand2 className="h-4 w-4 mr-2" />}
                {form.card_type === "COVER_DESIGN"
                  ? (form.cover_template === "A" ? "Generate cover variants" : "Compose cover")
                  : "Generate with Rex"}
              </Button>
            </>
          )}
          {step === "preview" && (
            <>
              <Button variant="ghost" onClick={() => setStep("prompt")} data-testid="wizard-back-prompt"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
              <Button onClick={createCard} disabled={creating} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="wizard-create-btn">
                {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                Create card
              </Button>
            </>
          )}
          {step === "cover_result" && (
            <>
              <Button variant="ghost" onClick={() => { setCoverResult(null); setStep("prompt"); }} data-testid="wizard-back-cover-result">
                <ArrowLeft className="h-4 w-4 mr-1" /> Generate another
              </Button>
              <Button onClick={() => handleClose(false)} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="wizard-cover-done-btn">
                Done
              </Button>
            </>
          )}
          {step === "compose_result" && (
            <>
              <Button variant="ghost" onClick={() => { setComposeResult(null); setStep("prompt"); }} data-testid="wizard-back-compose-result">
                <ArrowLeft className="h-4 w-4 mr-1" /> Compose another
              </Button>
              <Button onClick={() => handleClose(false)} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="wizard-compose-done-btn">
                Done
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Cover result panel (rendered inside the wizard when COVER_DESIGN
// generation succeeds). Variant grid + per-variant download buttons.
// Covers do NOT land on the planner board — they're downloadable PNGs.
function CoverResultPanel({ result, apiBase, composeMeta, onEditOnBoard }) {
  const received = (result.front_variants?.length || 0) + (result.back_variants?.length || 0);
  const expected = result.expected_total || received;
  const inProgress = received < expected;
  return (
    <div className="space-y-4" data-testid="wizard-cover-result-step">
      <div className="text-xs text-slate-600 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1">
        <div><strong>Run:</strong> <code className="text-[10px]">{result.run_id || "starting…"}</code></div>
        <div><strong>Model:</strong> <code className="text-[10px]">{result.primary_model || "…"}</code></div>
        <div><strong>Orientation:</strong> {result.orientation}</div>
        <div><strong>Scope:</strong> {result.scope === "front_back" ? "front + back" : "front only"}</div>
      </div>
      {inProgress && (
        <div className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 flex items-center gap-2"
             data-testid="wizard-cover-progress">
          <Loader2 className="h-3 w-3 animate-spin" />
          Generating cover variants — {received} of {expected} ready. Variants land as they finish; keep this modal open.
        </div>
      )}
      {result.scope === "front_back" && (
        <CoverSetSpread runId={result.run_id} apiBase={apiBase}
          front={(result.front_variants || []).find((v) => v.has_image)}
          back={(result.back_variants || []).find((v) => v.has_image)} />
      )}
      <CoverVariantGrid label="Front" runId={result.run_id} side="front" variants={result.front_variants} apiBase={apiBase} composeMeta={composeMeta} onEditOnBoard={onEditOnBoard} />
      {(result.back_variants?.length > 0 || result.scope === "front_back") && (
        <CoverVariantGrid label="Back" runId={result.run_id} side="back" variants={result.back_variants} apiBase={apiBase} composeMeta={composeMeta} onEditOnBoard={onEditOnBoard} />
      )}
      {result.rewritten_prompt && (
        <details className="text-xs text-slate-600">
          <summary className="cursor-pointer">Rewritten prompt</summary>
          <pre className="mt-2 p-2 bg-slate-50 border border-slate-200 rounded whitespace-pre-wrap font-mono text-[11px]">
            {result.rewritten_prompt}
          </pre>
        </details>
      )}
    </div>
  );
}

// Side-by-side "spread" preview of the matched cover set — back page on the
// left, front cover on the right, as they'd sit on an open flat-lay. Gives
// the operator an at-a-glance read of how the front + minimalist back pair
// works together before committing. Renders a waiting state until both sides
// have an image (the pair streams in one variant at a time).
function CoverSetSpread({ runId, apiBase, front, back }) {
  const frontSrc = front && runId ? `${apiBase}/v2/cover-design/runs/${runId}/preview/front/${front.variant_index}` : null;
  const backSrc = back && runId ? `${apiBase}/v2/cover-design/runs/${runId}/preview/back/${back.variant_index}` : null;
  const ready = !!frontSrc && !!backSrc;
  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3 space-y-2" data-testid="wizard-cover-set-spread">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-emerald-800">
        <Layers className="h-3.5 w-3.5" /> Cover set — matched spread
      </div>
      {ready ? (
        <div className="flex items-stretch justify-center gap-2 bg-white rounded-md p-3 shadow-inner">
          <Spread side="Back" src={backSrc} />
          <div className="w-px bg-slate-200 self-stretch" />
          <Spread side="Front" src={frontSrc} />
        </div>
      ) : (
        <div className="flex items-center gap-2 text-[11px] text-slate-500 px-1 py-3 justify-center">
          <Loader2 className="h-3 w-3 animate-spin" />
          Waiting for both pages to render — the spread appears once the front and back are ready.
        </div>
      )}
    </div>
  );
}

function Spread({ side, src }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <img src={src} alt={`${side} page`}
        className="h-48 w-auto object-contain rounded shadow-sm cursor-zoom-in border border-slate-200"
        onClick={() => window.open(src, "_blank", "noopener,noreferrer")}
        data-testid={`wizard-cover-set-spread-${side.toLowerCase()}`} />
      <span className="text-[10px] uppercase tracking-wide text-slate-500">{side}</span>
    </div>
  );
}

function CoverVariantGrid({ label, runId, side, variants, apiBase, composeMeta, onEditOnBoard }) {
  return (
    <div data-testid={`wizard-cover-variants-${side}`}>
      <h3 className="text-sm font-semibold text-slate-800 mb-2">{label}</h3>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {variants.map((v) => (
          <CoverVariantCell key={v.variant_index} v={v} runId={runId} side={side} apiBase={apiBase} composeMeta={composeMeta} onEditOnBoard={onEditOnBoard} />
        ))}
      </div>
    </div>
  );
}

function CoverVariantCell({ v, runId, side, apiBase, composeMeta, onEditOnBoard }) {
  const [downloading, setDownloading] = useState(false);
  const [editingOnBoard, setEditingOnBoard] = useState(false);
  const [composingA, setComposingA] = useState(false);
  const [composedA, setComposedA] = useState(null);  // {png_url, pdf_url}
  const state = v.safety_state;
  const isFlagged = state === "flagged";
  const badgeStyle = {
    passed: "bg-emerald-100 text-emerald-900 border-emerald-300",
    borderline: "bg-amber-100 text-amber-900 border-amber-300",
    flagged: "bg-red-100 text-red-900 border-red-300",
  }[state] || "bg-slate-100 text-slate-700 border-slate-300";
  const BadgeIcon = state === "passed" ? CheckCircle2 :
                    state === "borderline" ? ShieldAlert :
                    state === "flagged" ? Ban : AlertTriangle;
  const previewSrc = v.has_image
    ? `${apiBase}/v2/cover-design/runs/${runId}/preview/${side}/${v.variant_index}`
    : null;
  const downloadSrc = `${apiBase}/v2/cover-design/runs/${runId}/file/${side}/${v.variant_index}`;

  // Programmatic download — native `<a download>` was landing
  // inconsistently in the browser even though the server sent the
  // attachment header. fetch+blob guarantees the download triggers.
  const handleDownload = async () => {
    setDownloading(true);
    try {
      const resp = await fetch(downloadSrc, { credentials: "include" });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `cover_${runId}_${side}_${v.variant_index}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      toast.success(`Downloaded ${side} variant ${v.variant_index + 1}`);
    } catch (err) {
      console.error("download failed", err);
      toast.error(`Download failed: ${err.message}`);
    } finally {
      setDownloading(false);
    }
  };

  // Inline Template-A compose — uses the title/subtitle/client-logo
  // entered up-front in the wizard so the operator never leaves for the
  // Library. Composes THIS background variant into a finished A4 cover.
  const composeThisVariant = async () => {
    const title = (composeMeta?.title || "").trim();
    if (!title) { toast.error("Enter a title in the form above first."); return; }
    setComposingA(true);
    try {
      const fd = new FormData();
      fd.append("title", title);
      if ((composeMeta?.subtitle || "").trim()) fd.append("subtitle", composeMeta.subtitle.trim());
      if (composeMeta?.clientLogo) fd.append("client_logo", composeMeta.clientLogo);
      const resp = await fetch(`${apiBase}/v2/cover-design/runs/${runId}/compose-template-a/${side}/${v.variant_index}`, {
        method: "POST", credentials: "include", body: fd,
      });
      if (!resp.ok) {
        const e = await resp.json().catch(() => ({}));
        throw new Error(typeof e?.detail === "string" ? e.detail : `HTTP ${resp.status}`);
      }
      const body = await resp.json();
      setComposedA(body);
      toast.success("Composed Template A cover");
    } catch (err) {
      toast.error(`Compose failed: ${err.message}`);
    } finally {
      setComposingA(false);
    }
  };

  const downloadComposed = (url, ext) => {
    const base = apiBase.replace(/\/api$/, "");
    return blobDownload(`${base}${url}`, `cover_${runId}_${side}_${v.variant_index}_composed.${ext}`);
  };

  // Auto-compose: as soon as a variant has its background AND a title was
  // entered in the wizard, render the finished cover (title/subtitle/logo)
  // so the operator sees a completed cover immediately — "Edit on board"
  // remains for moving things around social-media style.
  useEffect(() => {
    if (v.has_image && (composeMeta?.title || "").trim() && !composedA && !composingA) {
      composeThisVariant();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v.has_image]);
  return (
    <div className="rounded border border-slate-200 p-2 space-y-2"
         data-testid={`wizard-cover-variant-${side}-${v.variant_index}`}>
      {previewSrc ? (
        <img src={previewSrc} alt={`${side} variant ${v.variant_index + 1}`}
             className="w-full h-32 object-cover rounded cursor-zoom-in"
             onClick={() => window.open(previewSrc, "_blank", "noopener,noreferrer")} />
      ) : (
        <div className="w-full h-32 bg-slate-50 rounded flex flex-col items-center justify-center text-xs text-slate-500">
          <ImageOff className="h-5 w-5 mb-1" />
          <span className="text-red-600 px-2 text-center text-[10px]">{v.error || "no image"}</span>
        </div>
      )}
      {state && (
        <div className={`text-[10px] px-2 py-0.5 rounded border flex items-start gap-1 ${badgeStyle}`}>
          <BadgeIcon className="h-3 w-3 flex-shrink-0 mt-0.5" />
          <span className="font-semibold uppercase tracking-wide">{state}</span>
        </div>
      )}
      {v.has_image ? (
        <button
          type="button"
          onClick={handleDownload}
          disabled={downloading}
          className={`block w-full text-center text-xs font-medium rounded px-2 py-1.5 disabled:opacity-50 ${isFlagged ? "bg-red-50 border border-red-300 text-red-800" : "bg-slate-900 hover:bg-slate-800 text-white"}`}
          data-testid={`wizard-cover-download-${side}-${v.variant_index}`}
        >
          {downloading
            ? <Loader2 className="w-3 h-3 inline animate-spin mr-1" />
            : <Download className="w-3 h-3 inline mr-1" />}
          Download {isFlagged ? "(flagged)" : "PNG"}
        </button>
      ) : (
        <div className="text-[10px] text-red-600 text-center">Generation failed</div>
      )}
      {/* Open this cover in the full editor (move/resize logos + text, fonts,
          shadows, abstract) — lands as an editable card on the board. */}
      {v.has_image && onEditOnBoard && (
        <button
          type="button"
          onClick={async () => { setEditingOnBoard(true); try { await onEditOnBoard(runId, side, v.variant_index); } finally { setEditingOnBoard(false); } }}
          disabled={editingOnBoard}
          className="block w-full text-center text-xs font-medium rounded px-2 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
          data-testid={`wizard-cover-edit-${side}-${v.variant_index}`}
        >
          {editingOnBoard ? <Loader2 className="w-3 h-3 inline animate-spin mr-1" /> : <Wand2 className="w-3 h-3 inline mr-1" />}
          Edit on board
        </button>
      )}
      {/* Inline Template-A compose — finish this background into a full
          cover without leaving for the Library. */}
      {v.has_image && composeMeta && !composedA && (
        <button
          type="button"
          onClick={composeThisVariant}
          disabled={composingA}
          className="block w-full text-center text-xs font-medium rounded px-2 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50"
          data-testid={`wizard-cover-compose-${side}-${v.variant_index}`}
        >
          {composingA ? <Loader2 className="w-3 h-3 inline animate-spin mr-1" /> : <Wand2 className="w-3 h-3 inline mr-1" />}
          Compose Template A
        </button>
      )}
      {composedA && (
        <div className="space-y-1.5 pt-1 border-t border-slate-200" data-testid={`wizard-cover-composed-${side}-${v.variant_index}`}>
          <img src={`${apiBase.replace(/\/api$/, "")}${composedA.png_url}`} alt="composed cover"
               className="w-full h-32 object-cover rounded cursor-zoom-in"
               onClick={() => window.open(`${apiBase.replace(/\/api$/, "")}${composedA.png_url}`, "_blank", "noopener,noreferrer")} />
          <div className="grid grid-cols-2 gap-1.5">
            <button type="button" onClick={() => downloadComposed(composedA.png_url, "png")}
              className="text-xs font-medium rounded px-2 py-1.5 bg-slate-900 hover:bg-slate-800 text-white"
              data-testid={`wizard-cover-composed-png-${side}-${v.variant_index}`}>
              <Download className="w-3 h-3 inline mr-1" /> PNG
            </button>
            <button type="button" onClick={() => downloadComposed(composedA.pdf_url, "pdf")}
              className="text-xs font-medium rounded px-2 py-1.5 bg-slate-700 hover:bg-slate-800 text-white"
              data-testid={`wizard-cover-composed-pdf-${side}-${v.variant_index}`}>
              <Download className="w-3 h-3 inline mr-1" /> PDF
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Programmatic blob download — native `<a download>` lands inconsistently
// across the proxy/browser combos, so fetch+blob guarantees the trigger.
async function blobDownload(url, filename) {
  try {
    const resp = await fetch(url, { credentials: "include" });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const blob = await resp.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    toast.success("Downloaded");
  } catch (err) {
    toast.error(`Download failed: ${err.message}`);
  }
}

// ─── Compose result panel — Templates B/C single-action output. Shows
// the finished cover with PNG + PDF downloads. No Library round-trip.
function ComposeResultPanel({ result, apiBase }) {
  const base = apiBase.replace(/\/api$/, "");
  const pngFull = `${base}${result.png_url}`;
  return (
    <div className="space-y-4" data-testid="wizard-compose-result-step">
      <div className="text-xs text-slate-600">
        <strong>Template {result.template}</strong> · <code className="text-[10px]">{result.run_id}</code>
      </div>
      <img src={pngFull} alt={`Template ${result.template} cover`}
           className="w-full max-h-[55vh] object-contain rounded border border-slate-200 bg-slate-50 cursor-zoom-in"
           onClick={() => window.open(pngFull, "_blank", "noopener,noreferrer")}
           data-testid="wizard-compose-result-image" />
      <div className="grid grid-cols-2 gap-3">
        <button type="button"
          onClick={() => blobDownload(pngFull, `resiligent_cover_${result.run_id}.png`)}
          className="text-sm font-medium rounded px-3 py-2 bg-slate-900 hover:bg-slate-800 text-white"
          data-testid="wizard-compose-download-png">
          <Download className="w-4 h-4 inline mr-1.5" /> Download PNG
        </button>
        <button type="button"
          onClick={() => blobDownload(`${base}${result.pdf_url}`, `resiligent_cover_${result.run_id}.pdf`)}
          className="text-sm font-medium rounded px-3 py-2 bg-slate-700 hover:bg-slate-800 text-white"
          data-testid="wizard-compose-download-pdf">
          <Download className="w-4 h-4 inline mr-1.5" /> Download PDF
        </button>
      </div>
    </div>
  );
}
